// ActionListPage — action list for the selected category.
// Rendered in the right panel of ActionLayout.
// Route: /t/:slug/actions (index route)

import { useState, useMemo, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { useSelectedActionCategory } from './ActionLayout'
import {
  actionsApi,
  actionLifecycleStateLabels,
  actionLifecycleStateColors,
  type Action,
} from '@/lib/api/actions'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CreateActionDialog } from './CreateActionDialog'
import {
  Plus,
  Search,
  ChevronRight,
  X,
  Link2,
  Shield,
  Zap,
  AlertTriangle,
} from 'lucide-react'
import { SortableHeader, useSortState, type SortState } from '@/components/shared/SortableHeader'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// --- Sort column type ---

type SortColumn = 'title' | 'owner' | 'due_date' | 'status'

function sortActions(actions: Action[], sort: SortState<SortColumn>): Action[] {
  if (!sort.column) return actions
  const sorted = [...actions].sort((a, b) => {
    let cmp = 0
    switch (sort.column) {
      case 'title':
        cmp = a.title.localeCompare(b.title)
        break
      case 'owner':
        cmp = (a.owner_display_name ?? '').localeCompare(b.owner_display_name ?? '')
        break
      case 'due_date':
        cmp = (a.due_date ?? '').localeCompare(b.due_date ?? '')
        break
      case 'status': {
        const order = ['draft', 'in_progress', 'closed']
        cmp = order.indexOf(a.lifecycle_state) - order.indexOf(b.lifecycle_state)
        break
      }
    }
    return sort.direction === 'desc' ? -cmp : cmp
  })
  return sorted
}

export function ActionListPage() {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const { categoryId, categoryName } = useSelectedActionCategory()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  function handleActionClick(actionId: string) {
    navigate({ to: `/t/${slug}/actions/${actionId}` })
  }

  if (!categoryId) {
    return <EmptySelection />
  }

  return (
    <>
      <ActionListPanel
        categoryId={categoryId}
        categoryName={categoryName}
        onActionClick={handleActionClick}
        onCreateClick={() => setCreateDialogOpen(true)}
        slug={slug}
      />

      <CreateActionDialog
        categoryId={categoryId}
        categoryName={categoryName}
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={(actionId) => {
          setCreateDialogOpen(false)
          handleActionClick(actionId)
        }}
      />
    </>
  )
}

// --- Empty state when no category is selected ---

function EmptySelection() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground">
      <div className="space-y-2 text-center">
        <ChevronRight className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm">Select a category from the tree to view its actions.</p>
      </div>
    </div>
  )
}

// --- Action list panel ---

interface ActionListPanelProps {
  categoryId: string
  categoryName: string
  onActionClick: (actionId: string) => void
  onCreateClick: () => void
  slug: string
}

function ActionListPanel({
  categoryId,
  categoryName,
  onActionClick,
  onCreateClick,
  slug,
}: ActionListPanelProps) {
  const api = useApi()
  const actApi = actionsApi(api)

  // Filter state
  const [nameFilter, setNameFilter] = useState('')

  // Sort state
  const [sort, , handleSort] = useSortState<SortColumn>()

  // Server-side filters
  const serverFilter = {
    category_id: categoryId,
    ...(nameFilter ? { search: nameFilter } : {}),
  }

  const {
    data: actions,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [...queryKeys.actions.all(slug), serverFilter],
    queryFn: () => actApi.list(serverFilter),
  })

  // Client-side sorting
  const displayActions = useMemo(() => {
    if (!actions) return []
    return sortActions(actions, sort)
  }, [actions, sort])

  const hasActiveFilters = nameFilter !== ''

  const clearFilters = useCallback(() => {
    setNameFilter('')
  }, [])

  return (
    <>
      {/* Header bar */}
      <div className="flex items-center justify-between gap-4 border-b p-4">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold">{categoryName}</h1>
          <p className="text-xs text-muted-foreground">
            {actions
              ? `${displayActions.length}${displayActions.length !== actions.length ? ` of ${actions.length}` : ''} action${actions.length !== 1 ? 's' : ''}`
              : ''}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button size="sm" onClick={onCreateClick}>
            <Plus className="mr-1 h-4 w-4" />
            New Action
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 border-b bg-muted/10 px-4 py-2">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter by title..."
            className="h-8 pl-8 text-sm"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={clearFilters}>
            <X className="mr-1 h-3 w-3" />
            Clear
          </Button>
        )}
      </div>

      {/* Column headers */}
      {actions && actions.length > 0 && (
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center border-b bg-muted/30 px-4 py-1.5 text-xs font-medium text-muted-foreground">
            <SortableHeader
              label="Action"
              column="title"
              sort={sort}
              onSort={handleSort}
              className="w-[30%] min-w-0"
            />
            <SortableHeader
              label="Owner"
              column="owner"
              sort={sort}
              onSort={handleSort}
              className="w-[14%]"
            />
            <SortableHeader
              label="Due Date"
              column="due_date"
              sort={sort}
              onSort={handleSort}
              className="w-[12%]"
            />
            <span className="w-[14%] text-center">Links</span>
            <SortableHeader
              label="Status"
              column="status"
              sort={sort}
              onSort={handleSort}
              className="w-20 justify-center"
            />
            <span className="w-20 text-right">Code</span>
          </div>
        </TooltipProvider>
      )}

      {/* Action list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && <TableSkeleton rows={6} />}

        {error && (
          <div className="p-4">
            <ErrorState
              message="Failed to load actions"
              technicalDetail={error.message}
              onRetry={() => refetch()}
            />
          </div>
        )}

        {actions && displayActions.length === 0 && (
          <div className="flex h-48 items-center justify-center text-muted-foreground">
            <p className="text-sm">
              {hasActiveFilters
                ? 'No actions match your filters.'
                : 'No actions in this category.'}
            </p>
          </div>
        )}

        {displayActions.length > 0 && (
          <div className="divide-y">
            {displayActions.map((act) => (
              <ActionListRow
                key={act.id}
                action={act}
                onClick={() => onActionClick(act.id)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  )
}

// --- Individual action row ---

function ActionListRow({
  action,
  onClick,
}: {
  action: Action
  onClick: () => void
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="flex cursor-pointer items-center px-4 py-3 transition-colors hover:bg-accent/30"
        onClick={onClick}
      >
        {/* Title + subtitle — 30% */}
        <div className="w-[30%] min-w-0">
          <div className="mb-0.5 truncate text-sm font-medium">{action.title}</div>
          <div className="truncate text-xs text-muted-foreground">
            {action.category_name}
          </div>
        </div>

        {/* Owner — 14% */}
        <div className="w-[14%] truncate text-xs text-muted-foreground">
          {action.owner_display_name ?? '—'}
        </div>

        {/* Due date — 12% */}
        <div className="w-[12%] text-xs">
          {action.due_date ? (
            <span className={cn(action.is_overdue && 'flex items-center gap-1 text-red-600')}>
              {action.is_overdue && <AlertTriangle className="h-3 w-3" />}
              {new Date(action.due_date + 'T00:00:00').toLocaleDateString()}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Links — 14% */}
        <div className="flex w-[14%] items-center justify-center gap-2 text-xs tabular-nums text-muted-foreground">
          {action.linked_risk_count > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-0.5">
                  <Link2 className="h-3 w-3" />
                  {action.linked_risk_count}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {action.linked_risk_count} linked risk
                  {action.linked_risk_count !== 1 ? 's' : ''}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
          {action.linked_control_count > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-0.5">
                  <Shield className="h-3 w-3" />
                  {action.linked_control_count}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {action.linked_control_count} linked control
                  {action.linked_control_count !== 1 ? 's' : ''}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
          {action.linked_event_count > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-0.5">
                  <Zap className="h-3 w-3" />
                  {action.linked_event_count}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {action.linked_event_count} linked event
                  {action.linked_event_count !== 1 ? 's' : ''}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Status */}
        <div className="flex w-20 justify-center">
          <Badge
            variant="outline"
            className={cn(
              'px-1.5 py-0 text-xs',
              actionLifecycleStateColors[action.lifecycle_state],
            )}
          >
            {actionLifecycleStateLabels[action.lifecycle_state] ?? action.lifecycle_state}
          </Badge>
        </div>

        {/* Short code — rightmost */}
        <div className="w-20 text-right">
          {action.short_code ? (
            <span className="font-mono text-xs text-muted-foreground">{action.short_code}</span>
          ) : (
            <span className="text-xs text-muted-foreground/40">—</span>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
